import { formatDate, formatDateTime, formatMoney, formatMonth, formatNumber } from '@blimo/shared';
import { cn } from '../lib/utils.js';
import { Badge } from '../components/badge.jsx';

/** Display meta per month status of `FinanceService::paymentHistory` (colour is never the only channel: label + symbol). */
export const PAYMENT_STATUS = {
  on_time: { label: 'pünktlich', symbol: '✓', cell: 'bg-success text-success-foreground' },
  late: { label: 'verspätet', symbol: '!', cell: 'bg-warning text-warning-foreground' },
  partial: { label: 'teilweise', symbol: '½', cell: 'bg-warning-subtle text-warning-subtle-foreground ring-1 ring-inset ring-warning' },
  missing: { label: 'fehlend', symbol: '✕', cell: 'bg-destructive text-destructive-foreground' },
  pending: { label: 'offen (Frist läuft)', symbol: '…', cell: 'bg-muted text-muted-foreground ring-1 ring-inset ring-border' },
  no_due: { label: 'kein Soll', symbol: '–', cell: 'bg-muted/60 text-muted-foreground' },
};

const CATEGORY = {
  regelmaessig: { label: 'regelmäßig', variant: 'success' },
  leicht_verspaetet: { label: 'leicht verspätet', variant: 'warning' },
  unregelmaessig: { label: 'unregelmäßig', variant: 'destructive' },
};

const MONTHS = ['Jan', 'Feb', 'Mär', 'Apr', 'Mai', 'Jun', 'Jul', 'Aug', 'Sep', 'Okt', 'Nov', 'Dez'];
/** "2026-10-01" → "Okt 26" (no timezone shift: parsed from the ISO string). */
const shortMonth = (period) => `${MONTHS[Number(period.slice(5, 7)) - 1]} ${period.slice(2, 4)}`;

function monthTitle(m) {
  const meta = PAYMENT_STATUS[m.status] ?? PAYMENT_STATUS.no_due;
  const parts = [`${formatMonth(m.period)}: ${meta.label}`, `Soll ${formatMoney(m.due)}`, `zugeordnet ${formatMoney(m.paid)}`];
  if (m.outstanding > 0) parts.push(`offen ${formatMoney(m.outstanding)}`);
  if (m.paid_on) parts.push(`ausgeglichen am ${formatDate(m.paid_on)}`);
  if (m.days_late) parts.push(`${m.days_late} Tage nach Fälligkeit`);
  return parts.join(' · ');
}

/**
 * Compact 12-month payment timeline (Zahlungsverlauf) – display only, all values computed by the backend
 * (GET admin/tenancies/{id}/payment-history, GET portal/units/{id}/payment-history).
 * @param {{ history: any, className?: string, showRules?: boolean }} props
 */
export function PaymentTimeline({ history, className, showRules = false }) {
  if (!history) return null;
  if (!history.available) {
    return (
      <p className={cn('text-[0.8125rem] text-muted-foreground', className)}>
        Für diese Liegenschaft werden noch keine Zahlungseingänge aus der Buchhaltung geliefert.
      </p>
    );
  }
  const { months = [], summary } = history;
  const category = CATEGORY[summary?.category];
  const used = Object.keys(PAYMENT_STATUS).filter((s) => months.some((m) => m.status === s));

  return (
    <div className={cn('flex flex-col gap-3', className)} data-testid="payment-timeline">
      <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-[0.8125rem]">
        {category ? (
          <Badge variant={category.variant}>
            Zahlungsverhalten {category.label}
            {summary.score !== null && <span className="numeric">({summary.score}/100)</span>}
          </Badge>
        ) : (
          <Badge variant="neutral">Noch keine bewertbaren Monate</Badge>
        )}
        {summary?.arrears > 0 && (
          <span>
            Rückstand <span className="numeric font-medium text-destructive">{formatMoney(summary.arrears)}</span>
            {summary.months_behind !== null && (
              <span className="numeric text-muted-foreground"> (≈ {formatNumber(summary.months_behind, { maximumFractionDigits: 1 })} Monatsmieten)</span>
            )}
          </span>
        )}
        {summary?.average_days_late !== null && summary?.average_days_late !== undefined && (
          <span className="text-muted-foreground">
            Ø <span className="numeric">{formatNumber(summary.average_days_late, { maximumFractionDigits: 1 })}</span> Tage verspätet
          </span>
        )}
      </div>

      <ol className="grid grid-cols-6 gap-1.5 sm:grid-cols-12" aria-label="Zahlungsverlauf je Monat">
        {months.map((m) => {
          const meta = PAYMENT_STATUS[m.status] ?? PAYMENT_STATUS.no_due;
          const title = monthTitle(m);
          return (
            <li key={m.period} className="flex min-w-0 flex-col items-center gap-1" title={title}>
              <span
                aria-hidden="true"
                className={cn('flex h-7 w-full items-center justify-center rounded-md text-xs font-semibold', meta.cell)}
                data-status={m.status}
              >
                {meta.symbol}
              </span>
              <span aria-hidden="true" className="numeric text-[0.6875rem] text-muted-foreground">
                {shortMonth(m.period)}
              </span>
              <span className="sr-only">{title}</span>
            </li>
          );
        })}
      </ol>

      <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-[0.6875rem] text-muted-foreground" aria-hidden="true">
        {used.map((s) => (
          <span key={s} className="inline-flex items-center gap-1">
            <span className={cn('inline-flex size-3.5 items-center justify-center rounded-sm text-[0.5625rem] font-semibold', PAYMENT_STATUS[s].cell)}>
              {PAYMENT_STATUS[s].symbol}
            </span>
            {PAYMENT_STATUS[s].label}
          </span>
        ))}
        {history.synced_at && <span className="ms-auto">Stand Buchhaltung {formatDateTime(history.synced_at)}</span>}
      </div>

      {showRules && history.rules?.length > 0 && (
        <details className="text-[0.75rem] text-muted-foreground">
          <summary className="cursor-pointer select-none">Wie wird das berechnet?</summary>
          <ul className="mt-1 list-disc space-y-0.5 ps-4">
            {history.rules.map((r) => (
              <li key={r}>{r}</li>
            ))}
          </ul>
        </details>
      )}
    </div>
  );
}
